import { useCallback, useEffect, useState } from "react";
import { Loader2, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const SOURCES = [
  ["", "Все"],
  ["appway", "AppWay"],
  ["prosto", "ProstoApp"],
  ["worker", "Боты воркеров"],
];
const TYPES = {
  start: ["/start", "bg-sky-500/15 text-sky-300"],
  message: ["Сообщение", "bg-slate-500/15 text-slate-300"],
  privacy: ["Политика", "bg-slate-500/15 text-slate-300"],
  open: ["Открыл витрину", "bg-indigo-500/15 text-indigo-300"],
  view: ["Страница", "bg-white/5 text-slate-300"],
  view_app: ["Приложение", "bg-violet-500/15 text-violet-300"],
  search: ["Поиск", "bg-amber-500/15 text-amber-300"],
  click: ["Нажал", "bg-white/5 text-slate-400"],
  issue_request: ["Запрос выдачи", "bg-blue-500/15 text-blue-300"],
  issue_ok: ["Выдан ✓", "bg-emerald-500/15 text-emerald-300"],
  issue_failed: ["Выдача ✗", "bg-red-500/15 text-red-300"],
  revoked: ["Отозвал", "bg-orange-500/15 text-orange-300"],
  expired: ["Время вышло", "bg-orange-500/15 text-orange-300"],
};
const SRC_LABEL = { appway: "AppWay", prosto: "ProstoApp", worker: "Воркер" };

const fmtTime = (iso) =>
  new Date(iso).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });

const userName = (u) => (u.id ? (u.username ? `@${u.username}` : u.name || `id ${u.id}`) : `гость ${(u.anon || "").slice(0, 6)}`);

function LogRow({ e, onUser }) {
  const [tl, tc] = TYPES[e.type] || [e.type, "bg-white/5 text-slate-300"];
  return (
    <div data-testid={`admin-log-${e.id}`} className="p-3 rounded-2xl bg-white/[0.03] border border-white/10">
      <div className="flex items-center gap-2 text-[11px] text-slate-500">
        <span className="font-mono">{fmtTime(e.at)}</span>
        <span>·</span>
        <span>{SRC_LABEL[e.source] || e.source}</span>
        {e.bot && <span className="truncate">· {e.bot}</span>}
        {e.worker && <span className="truncate">· {e.worker}</span>}
      </div>
      <div className="mt-1.5 flex items-center gap-2 flex-wrap">
        <button
          data-testid={`admin-log-user-${e.id}`}
          onClick={() => (e.user.id || e.user.anon) && onUser(e.user)}
          className="text-sm font-semibold text-blue-300 hover:underline"
        >
          {userName(e.user)}
        </button>
        {e.user.id && e.user.username && e.user.name && <span className="text-xs text-slate-500">{e.user.name}</span>}
        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${tc}`}>{tl}</span>
      </div>
      {(e.label || e.details?.app) && (
        <div className="mt-1 text-sm text-slate-300 break-words">
          {e.label}
          {e.details?.app && !e.label?.includes(e.details.app) && <span className="text-slate-500"> · {e.details.app}</span>}
        </div>
      )}
      {e.details?.platform && <div className="text-[11px] text-slate-500 mt-0.5">устройство: {e.details.platform}</div>}
    </div>
  );
}

function Stats({ s }) {
  const cells = [
    ["Событий 24ч", s.events_24h],
    ["Юзеров TG", s.tg_users_24h],
    ["Открытий", s.opens_24h],
    ["Выдач", s.issues_24h],
  ];
  return (
    <div className="grid grid-cols-4 gap-2 mb-3" data-testid="admin-logs-stats">
      {cells.map(([l, v]) => (
        <div key={l} className="rounded-xl bg-white/5 border border-white/10 py-2 text-center">
          <div className="font-display font-bold">{v}</div>
          <div className="text-[10px] text-slate-500">{l}</div>
        </div>
      ))}
    </div>
  );
}

export function AdminLogs() {
  const [source, setSource] = useState("");
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  const [user, setUser] = useState(null);
  const [data, setData] = useState({ items: [], stats: null });
  const [loading, setLoading] = useState(false);
  const [more, setMore] = useState(true);

  const params = useCallback(
    (last) => ({
      source,
      type,
      q,
      before: last?.at || "",
      before_id: last?.id || "",
      limit: 100,
      ...(user?.id ? { user_id: user.id } : user ? { anon: user.anon } : {}),
    }),
    [source, type, q, user]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.adminLogs(params());
      setData(d);
      setMore(d.items.length === 100);
    } catch {
      toast.error("Не удалось загрузить логи");
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const loadMore = async () => {
    const last = data.items[data.items.length - 1];
    if (!last) return;
    try {
      const d = await api.adminLogs(params(last));
      setData((p) => ({ ...p, items: [...p.items, ...d.items] }));
      setMore(d.items.length === 100);
    } catch {
      toast.error("Не удалось загрузить логи");
    }
  };

  return (
    <div data-testid="admin-logs">
      {data.stats && <Stats s={data.stats} />}
      <div className="flex gap-2 overflow-x-auto no-scrollbar mb-2">
        {SOURCES.map(([k, l]) => (
          <button
            key={k || "all"}
            data-testid={`admin-logs-source-${k || "all"}`}
            onClick={() => setSource(k)}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition ${source === k ? "bg-blue-500 text-white" : "bg-white/5 text-slate-400"}`}
          >
            {l}
          </button>
        ))}
      </div>
      <div className="flex gap-2 mb-3">
        <select
          data-testid="admin-logs-type"
          value={type}
          onChange={(e) => setType(e.target.value)}
          className="shrink-0 rounded-xl bg-white/5 border border-white/10 px-2 py-2 text-xs text-slate-200"
        >
          <option value="">Все события</option>
          {Object.entries(TYPES).map(([k, [l]]) => (
            <option key={k} value={k}>{l}</option>
          ))}
        </select>
        <input
          data-testid="admin-logs-query"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="@username, имя, приложение…"
          className="flex-1 min-w-0 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none"
        />
        <button
          data-testid="admin-logs-refresh"
          onClick={load}
          className="shrink-0 w-10 grid place-items-center rounded-xl bg-white/5 text-slate-300"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
        </button>
      </div>
      {user && (
        <div data-testid="admin-logs-user-filter" className="mb-3 flex items-center gap-2 rounded-xl bg-blue-500/10 border border-blue-500/30 px-3 py-2 text-sm">
          <span className="flex-1">Путь пользователя: <b>{userName(user)}</b>{user.id ? ` · id ${user.id}` : ""}</span>
          <button data-testid="admin-logs-user-clear" onClick={() => setUser(null)} className="text-slate-400">
            <X size={16} />
          </button>
        </div>
      )}
      <div className="space-y-2">
        {data.items.map((e) => (
          <LogRow key={e.id} e={e} onUser={setUser} />
        ))}
        {!loading && !data.items.length && (
          <div data-testid="admin-logs-empty" className="py-10 text-center text-sm text-slate-500">Событий пока нет</div>
        )}
      </div>
      {more && data.items.length > 0 && (
        <button
          data-testid="admin-logs-more"
          onClick={loadMore}
          className="w-full mt-3 py-3 rounded-2xl text-sm font-semibold bg-white/5 text-slate-300"
        >
          Показать ещё
        </button>
      )}
      <div className="mt-3 text-[11px] text-slate-600 text-center">Логи хранятся 30 дней</div>
    </div>
  );
}
